import React, { useEffect, useState } from "react";
import { Apple, Download, MonitorSmartphone, RefreshCw, Smartphone, UserCheck, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import SkyBackdrop from "../components/brand/SkyBackdrop";
import PhoneMockup from "../components/getapp/PhoneMockup";
import QrCard from "../components/getapp/QrCard";
import StoreBadge from "../components/getapp/StoreBadge";
import InstallCard from "../components/getapp/InstallCard";
import { API, api } from "../lib/api";
import { detectDevice, isStandalone, useInstallPrompt } from "../hooks/useInstallPrompt";
import { trackAppTap } from "../lib/appStats";

const ANDROID_STEPS = [
  <>Tap <b className="text-slate-900">Download for Android</b> below.</>,
  <>Open the downloaded <b className="text-slate-900">Ishukart.apk</b> from the notification or your Downloads.</>,
  <>If your phone asks, tap <b className="text-slate-900">Settings → Allow from this source</b>, then go back.</>,
  <>Tap <b className="text-slate-900">Install</b>, then <b className="text-slate-900">Open</b> and sign in with your Ishukart account.</>,
];
const IOS_STEPS = [
  <>Open <b className="text-slate-900">ishukart.org/get-app</b> in <b className="text-slate-900">Safari</b> on your iPhone or iPad.</>,
  <>Tap the <b className="text-slate-900">Share</b> button — the square with an arrow pointing up.</>,
  <>Scroll down and tap <b className="text-slate-900">Add to Home Screen</b>.</>,
  <>Tap <b className="text-slate-900">Add</b>. Ishukart now sits on your home screen and opens full-screen like any app.</>,
];
const PERKS = [
  [UserCheck, "Same account, same projects", "Sign in with the account you already use — every project is right there."],
  [RefreshCw, "Always up to date", "New features reach the app by themselves. Nothing to reinstall."],
  [Wallet, "Free to install", "You only pay for the credits you use to build."],
];

const ApkButton = ({ android, testid }) =>
  android?.available ? (
    <a href={`${API}${android.download_url.replace(/^\/api/, "")}`} download="Ishukart.apk" data-testid={testid}
      onClick={() => { trackAppTap("get_app_page"); toast.success("Downloading Ishukart for Android", { description: "Open the file when it finishes to install." }); }}
      className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-sky-500 text-white font-semibold shadow-sm hover:bg-sky-600 active:bg-sky-700 hover:-translate-y-0.5 transition-[transform,background-color] duration-200">
      <Download size={18} /> Download for Android
    </a>
  ) : (
    <span className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-200 text-slate-500 font-semibold" data-testid={testid}>Android app coming soon</span>
  );

const InstallHere = ({ install, testid, label = "Install from this browser" }) => (
  <button onClick={() => { trackAppTap("get_app_page"); install(); }} data-testid={testid}
    className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white ring-1 ring-slate-200 text-slate-800 font-medium hover:bg-slate-50 transition-colors">
    <MonitorSmartphone size={17} /> {label}
  </button>
);

const HeroCta = ({ device, android, canInstall, onInstall }) => {
  if (device === "android") return (
    <div className="flex flex-wrap items-center gap-3">
      <ApkButton android={android} testid="getapp-hero-apk-btn" />
      {canInstall && <InstallHere install={onInstall} testid="getapp-hero-install-btn" />}
    </div>
  );
  if (device === "ios") return (
    <a href="#iphone" onClick={() => trackAppTap("get_app_page")} data-testid="getapp-hero-ios-btn" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-sky-500 text-white font-semibold hover:bg-sky-600 hover:-translate-y-0.5 transition-[transform,background-color] duration-200">
      <Apple size={18} /> Add to iPhone home screen
    </a>
  );
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a href="#android" data-testid="getapp-hero-android-link" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-sky-500 text-white font-semibold hover:bg-sky-600 transition-colors"><Smartphone size={18} /> Android</a>
      <a href="#iphone" data-testid="getapp-hero-iphone-link" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-700 transition-colors"><Apple size={18} /> iPhone</a>
      {canInstall && <InstallHere install={onInstall} testid="getapp-hero-install-btn" label="Install on this computer" />}
    </div>
  );
};

export default function GetApp() {
  const [info, setInfo] = useState(null);
  const [device] = useState(detectDevice);
  const [standalone] = useState(isStandalone);
  const { canInstall, install, installed } = useInstallPrompt();
  const pageUrl = `${window.location.origin}/get-app`;

  useEffect(() => { api.mobileApp().then(setInfo).catch(() => setInfo({ android: {}, ios: {} })); }, []);
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
  }, []);
  const onInstall = async () => { if (await install()) toast.success("Ishukart is installed — find it on your home screen"); };
  const android = info?.android;

  return (
    <div className="min-h-screen bg-[#F5F8FC] text-slate-700" data-testid="get-app-page">
      <SiteNav />
      <section className="relative pt-28 sm:pt-36 pb-16 overflow-hidden">
        <SkyBackdrop />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/70 backdrop-blur ring-1 ring-white text-xs font-semibold text-sky-700" data-testid="getapp-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" /> Mobile app · Android &amp; iPhone
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-slate-900">
              Ishukart, right in your <span className="text-sky-600">pocket</span>.
            </h1>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Build, preview and publish apps from your phone. Your projects, credits and published sites — all one tap away.
            </p>
            {(standalone || installed) && (
              <p className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-emerald-700" data-testid="getapp-installed-note"><CheckCircle2 size={16} /> You're using the Ishukart app.</p>
            )}
            <div className="mt-8"><HeroCta device={device} android={android} canInstall={canInstall} onInstall={onInstall} /></div>
            {device === "android" && android?.available && (
              <p className="mt-3 text-xs font-mono text-slate-500" data-testid="getapp-apk-meta">Version {android.version} · {android.size_mb} MB · Free</p>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <StoreBadge store="play" url={android?.play_store_url} testid="getapp-play-badge" />
              <StoreBadge store="apple" url={info?.ios?.app_store_url} testid="getapp-appstore-badge" />
            </div>
            {device === "desktop" && <div className="mt-10 max-w-md hidden sm:block"><QrCard url={pageUrl} /></div>}
          </div>
          <PhoneMockup />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid md:grid-cols-2 gap-6">
        <InstallCard id="android" icon={Smartphone} title="Android" desc="Phones and tablets with Android 7 or newer" steps={ANDROID_STEPS} highlight={device === "android"} testid="getapp-android-card">
          <ApkButton android={android} testid="getapp-android-apk-btn" />
          {android?.available && <span className="text-xs font-mono text-slate-400" data-testid="getapp-android-meta">v{android.version} · {android.size_mb} MB</span>}
          <p className="w-full text-xs text-slate-500">Prefer no download? Open ishukart.org in Chrome, tap <b>⋮</b> → <b>Install app</b>.</p>
        </InstallCard>
        <InstallCard id="iphone" icon={Apple} title="iPhone & iPad" desc="Works in Safari — no App Store needed" steps={IOS_STEPS} highlight={device === "ios"} testid="getapp-ios-card">
          <StoreBadge store="apple" url={info?.ios?.app_store_url} testid="getapp-ios-appstore-badge" />
        </InstallCard>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid sm:grid-cols-3 gap-4">
          {PERKS.map(([Icon, title, text], i) => (
            <div key={title} className="rounded-2xl bg-white/70 ring-1 ring-slate-200 p-5 fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`getapp-perk-${i}`}>
              <Icon size={18} className="text-sky-600" />
              <p className="mt-3 font-display font-semibold text-slate-900">{title}</p>
              <p className="mt-1 text-sm text-slate-500 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
        {device !== "desktop" && <div className="mt-10 max-w-md"><QrCard url={pageUrl} compact testid="getapp-share-qr" title="Share with a friend" desc="Let someone scan this code to get Ishukart on their phone." /></div>}
      </section>
      <SiteFooter />
    </div>
  );
}
